import { GridColumn, GridDataRow, GridTableLayout, PageHeaderLayout, column, simpleHeader } from "@homebound/beam";
import { useMemo } from "react";
import { ChangeTypesQuery, useChangeTypesQuery } from "src/components/change-log/ChangeTypesPage.generated";
import { ChangeType } from "src/generated/graphqlTypes";
import { changeLogRoute } from "src/routing/domains/changeLogPaths";

export function ChangeTypesPage() {
  const { data } = useChangeTypesQuery();

  const columns = useMemo(() => createColumns(), []);

  const rows: GridDataRow<Row>[] = useMemo(() => createRows(data?.enumDetails), [data?.enumDetails]);

  return (
    <PageHeaderLayout
      pageHeader={{
        title: "Change Types",
        breadcrumbs: { breadcrumbs: [{ href: changeLogRoute.url(), label: "Change Log" }] },
      }}
    >
      <GridTableLayout hideEditColumns tableProps={{ columns, rows }} />
    </PageHeaderLayout>
  );
}

const ChangeTypeToExampleMap: Record<ChangeType, string> = {
  [ChangeType.ProgramChange]: "Square footage, bed/bath counts",
  [ChangeType.ArchitechturalDesign]: "Center windows in bedrooms #2 and #5",
  [ChangeType.StructuralDesign]: "Insufficient framing specified for new window",
  [ChangeType.MepDesign]: "Add can light in prep pantry",
  [ChangeType.AddRemoveModifyProducts]: "Remove the hand-held shower in the primary bathrooms, for a partiucular lot",
  [ChangeType.ModifyOptionsProgram]: "New option added to a design package",
  [ChangeType.NewDesignPackage]: "Market refresh to include alternate schemes",
  [ChangeType.UpdateExistingDesignPackage]: "Changing a countertop selection due to negative buyer feedback",
  [ChangeType.NewExteriorStyle]: "Change to the exterior elevation",
  [ChangeType.ImplementationError]: "Incorrect doors installed",
  [ChangeType.ProductRefresh]: "-",
  [ChangeType.Na]: "-",
};

type HeaderRow = { kind: "header" };
type DataRow = { kind: "data"; data: { changeType: string; example: string } };

type Row = HeaderRow | DataRow;

const createColumns = (): GridColumn<Row>[] => [
  column<Row>({ header: "Change Type", data: ({ changeType }) => ({ content: () => changeType }), w: "380px" }),
  column<Row>({ header: "Example", data: ({ example }) => ({ content: () => example }) }),
];

function createRows(enumDetails?: ChangeTypesQuery["enumDetails"]): GridDataRow<Row>[] {
  const changeTypes = enumDetails?.changeType ?? [];
  return [
    simpleHeader,
    ...changeTypes.map((ct) => ({
      id: ct.code,
      kind: "data" as const,
      data: { changeType: ct.name, example: ChangeTypeToExampleMap[ct.code as ChangeType] },
    })),
  ];
}
